import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { addressLine } from "../components/AddressFields/AddressFields";
import Loader from "../components/Loader/Loader";
import OrderActions, { OrderBreakdown } from "../components/OrderActions/OrderActions";
import { useAuth } from "../context/AuthContext";
import * as authApi from "../api/authApi";

import {
  FaUser,
  FaShoppingBag,
  FaMapMarkerAlt,
  FaSignOutAlt,
  FaEdit,
  FaPlus,
  FaTrash,
  FaCheckCircle,
  FaLock,
} from "react-icons/fa";

import API from "../config";
import "./Address.css";

const Youraccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, accessToken, isLoggedIn, initializing, logout } = useAuth();

  useEffect(() => {
    if (!initializing && !isLoggedIn) {
      navigate("/login", { state: { redirectTo: "/account" } });
    }
  }, [initializing, isLoggedIn, navigate]);

  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "profile"
  );
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] =
    useState(false);
  const [addressError, setAddressError] =
    useState("");
  const [showAddressForm, setShowAddressForm] =
    useState(false);
  const [savingAddress, setSavingAddress] =
    useState(false);
  const [addressForm, setAddressForm] = useState({
    fullName: "",
    phone: "",
    line1: "",
    line2: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    isDefault: false,
  });

  const [editingAddressId, setEditingAddressId] =
    useState(null);

  const [editAddressForm, setEditAddressForm] =
    useState({
      fullName: "",
      phone: "",
      line1: "",
      line2: "",
      landmark: "",
      isDefault: false,
    });

  const [updatingAddress, setUpdatingAddress] =
    useState(false);

  const [deletingAddressId, setDeletingAddressId] =
    useState(null);
const [orders, setOrders] = useState([]);
const [loadingOrders, setLoadingOrders] = useState(false);
const [orderError, setOrderError] = useState("");
const [expandedOrderId, setExpandedOrderId] = useState(null);
const [trackingByOrder, setTrackingByOrder] = useState({});
const [loadingTracking, setLoadingTracking] = useState(false);

const [passwordForm, setPasswordForm] = useState({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
});
const [passwordError, setPasswordError] = useState("");
const [passwordSuccess, setPasswordSuccess] = useState("");
const [changingPassword, setChangingPassword] = useState(false);

const handlePasswordFieldChange = (e) => {
  const { name, value } = e.target;
  setPasswordForm((prev) => ({ ...prev, [name]: value }));
};

const handleChangePassword = async (e) => {
  e.preventDefault();
  setPasswordError("");
  setPasswordSuccess("");

  const { currentPassword, newPassword, confirmPassword } = passwordForm;

  if (!currentPassword || !newPassword || !confirmPassword) {
    setPasswordError("Please fill in all fields.");
    return;
  }

  if (newPassword !== confirmPassword) {
    setPasswordError("New password and confirm password do not match.");
    return;
  }

  try {
    setChangingPassword(true);

    const { ok, data } = await authApi.changePassword(
      currentPassword,
      newPassword,
      accessToken
    );

    if (!ok) {
      throw new Error(data?.message || "Could not change password.");
    }

    setPasswordSuccess(
      "Password changed successfully. Please login again."
    );
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    // Changing the password revokes every session on the backend
    // (including this one), so log out here too instead of leaving the
    // UI showing a now-invalid session.
    setTimeout(async () => {
      await logout();
      navigate("/login", { state: { redirectTo: "/account" } });
    }, 1500);
  } catch (error) {
    setPasswordError(error.message || "Could not change password.");
  } finally {
    setChangingPassword(false);
  }
};

const handleViewOrder = async (orderId) => {
  if (expandedOrderId === orderId) {
    setExpandedOrderId(null);
    return;
  }

  setExpandedOrderId(orderId);

  if (trackingByOrder[orderId]) {
    return;
  }

  try {
    setLoadingTracking(true);

    const response = await fetch(`${API}/orders/${orderId}/tracking`);
    const result = await response.json();

    if (result?.success) {
      setTrackingByOrder((prev) => ({
        ...prev,
        [orderId]: result.data,
      }));
    }
  } catch (error) {
    console.error("ORDER TRACKING ERROR:", error);
  } finally {
    setLoadingTracking(false);
  }
};

const fetchOrders = async () => {
  try {
    setLoadingOrders(true);
    setOrderError("");

    const token = getToken();

    if (!token) {
      setOrderError("Login session nahi mili. Please login again.");
      return;
    }

    const response = await fetch(`${API}/orders/my-orders`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();

    console.log("GET MY ORDERS RESPONSE:", result);

    if (!response.ok) {
      throw new Error(
        result?.message ||
          `Failed to load orders (${response.status})`
      );
    }

    if (result.success === true) {
      setOrders(Array.isArray(result.data) ? result.data : []);
    } else {
      setOrders([]);
      setOrderError(
        result?.message || "Unable to load orders."
      );
    }
  } catch (error) {
    console.error("ORDERS GET ERROR:", error);

    setOrderError(
      error.message || "Something went wrong while loading orders."
    );
  } finally {
    setLoadingOrders(false);
  }
};

  const getToken = () => {
    return accessToken;
  };

  const fetchAddresses = async () => {
    try {
      setLoadingAddresses(true);
      setAddressError("");

      const token = getToken();

      if (!token) {
        setAddressError(
          "Login session nahi mili. Please login again."
        );

        setLoadingAddresses(false);
        return;
      }

      const response = await fetch(
        `${API}/addresses`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      console.log(
        "GET ADDRESSES RESPONSE:",
        result
      );

      if (!response.ok) {
        throw new Error(
          result?.message ||
            `Failed to load addresses (${response.status})`
        );
      }

      if (result.success) {
        let addressData = [];

        if (Array.isArray(result.data)) {
          addressData = result.data;
        } else if (
          Array.isArray(result.data?.addresses)
        ) {
          addressData = result.data.addresses;
        } else if (result.data?.uuid) {
          addressData = [result.data];
        }

        setAddresses(addressData);
      } else {
        setAddresses([]);

        setAddressError(
          result.message ||
            "Unable to load addresses."
        );
      }
    } catch (error) {
      console.error(
        "Address GET Error:",
        error
      );

      setAddressError(
        error.message ||
          "Unable to load addresses."
      );
    } finally {
      setLoadingAddresses(false);
    }
  };
useEffect(() => {
  if (activeTab === "addresses") {
    fetchAddresses();
  }

  if (activeTab === "orders") {
    fetchOrders();
  }
}, [activeTab]);

  const handleAddressChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setAddressForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const resetAddressForm = () => {
    setAddressForm({
      fullName: "",
      phone: "",
      line1: "",
      line2: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      isDefault: false,
    });
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();

    setSavingAddress(true);
    setAddressError("");

    try {
      const token = getToken();

      if (!token) {
        setAddressError(
          "Login session nahi mili. Please login again."
        );
        return;
      }

      if (
        !addressForm.fullName.trim() ||
        !addressForm.phone.trim() ||
        !addressForm.line1.trim() ||
        !addressForm.line2.trim() ||
        !addressForm.city.trim() ||
        !addressForm.state.trim() ||
        !addressForm.pincode.trim()
      ) {
        setAddressError(
          "Please fill all address fields."
        );
        return;
      }

      if (
        !/^\d{6}$/.test(
          addressForm.pincode
        )
      ) {
        setAddressError(
          "Please enter a valid 6 digit pincode."
        );
        return;
      }

      if (
        !/^\d{10}$/.test(
          addressForm.phone
        )
      ) {
        setAddressError(
          "Please enter a valid 10 digit phone number."
        );
        return;
      }

      const requestBody = {
        fullName:
          addressForm.fullName.trim(),

        phone:
          addressForm.phone.trim(),

        line1:
          addressForm.line1.trim(),

        line2:
          addressForm.line2.trim(),

        landmark:
          addressForm.landmark.trim(),

        city:
          addressForm.city.trim(),

        state:
          addressForm.state.trim(),

        pincode:
          addressForm.pincode.trim(),

        isDefault:
          addressForm.isDefault,
      };

      console.log(
        "POST ADDRESS BODY:",
        requestBody
      );

      const response = await fetch(
        `${API}/addresses`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body:
            JSON.stringify(requestBody),
        }
      );

      const result =
        await response.json();

      console.log(
        "POST ADDRESS RESPONSE:",
        result
      );

      if (!response.ok) {
        throw new Error(
          result?.message ||
            `Address API failed with status ${response.status}`
        );
      }

      if (result.success === true) {
        alert(
          "Address added successfully!"
        );

        resetAddressForm();

        setShowAddressForm(false);

        await fetchAddresses();
      } else {
        throw new Error(
          result?.message ||
            "Unable to save address."
        );
      }
    } catch (error) {
      console.error(
        "ADDRESS POST ERROR:",
        error
      );

      setAddressError(
        error.message ||
          "Something went wrong while saving address."
      );
    } finally {
      setSavingAddress(false);
    }
  };

  // =====================================================
  // EDIT ADDRESS BUTTON
  // =====================================================

  const handleEditAddress = (address) => {
    console.log(
      "EDIT ADDRESS:",
      address
    );

    setEditingAddressId(
      address.uuid
    );

    setEditAddressForm({
      fullName:
        address.fullName || "",

      phone:
        address.phone || "",

      line1:
        address.line1 || "",

      line2:
        address.line2 || "",

      landmark:
        address.landmark || "",

      isDefault:
        Boolean(address.isDefault),
    });

    setAddressError("");
  };

  // =====================================================
  // EDIT ADDRESS INPUT CHANGE
  // =====================================================

  const handleEditAddressChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setEditAddressForm((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  // =====================================================
  // UPDATE ADDRESS
  // PUT /api/v1/addresses/:uuid
  // =====================================================

  const handleUpdateAddress = async (e) => {
    e.preventDefault();

    if (!editingAddressId) {
      return;
    }

    setUpdatingAddress(true);
    setAddressError("");

    try {
      const token = getToken();

      if (!token) {
        setAddressError(
          "Login session nahi mili. Please login again."
        );
        return;
      }

      // VALIDATION

      if (
        !editAddressForm.fullName.trim() ||
        !editAddressForm.phone.trim() ||
        !editAddressForm.line1.trim()
      ) {
        setAddressError(
          "Please fill all address fields."
        );
        return;
      }

      if (
        !/^\d{10}$/.test(
          editAddressForm.phone
        )
      ) {
        setAddressError(
          "Please enter a valid 10 digit phone number."
        );
        return;
      }

      // BODY

      const requestBody = {
        fullName:
          editAddressForm.fullName.trim(),

        phone:
          editAddressForm.phone.trim(),

        line1:
          editAddressForm.line1.trim(),

        // sent even when empty, so clearing the field clears it
        line2:
          editAddressForm.line2.trim(),

        landmark:
          editAddressForm.landmark.trim(),

        isDefault:
          editAddressForm.isDefault,
      };

      console.log(
        "UPDATE ADDRESS UUID:",
        editingAddressId
      );

      console.log(
        "UPDATE ADDRESS BODY:",
        requestBody
      );

      // PUT API

      const response = await fetch(
        `${API}/addresses/${editingAddressId}`,
        {
          method: "PUT",

          headers: {
            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body:
            JSON.stringify(
              requestBody
            ),
        }
      );

      const result =
        await response.json();

      console.log(
        "UPDATE ADDRESS RESPONSE:",
        result
      );

      if (!response.ok) {
        throw new Error(
          result?.message ||
            `Update address failed (${response.status})`
        );
      }

      if (result.success === true) {
        alert(
          "Address updated successfully!"
        );

        // CLOSE EDIT FORM

        setEditingAddressId(null);

        setEditAddressForm({
          fullName: "",
          phone: "",
          line1: "",
          line2: "",
          landmark: "",
          isDefault: false,
        });

        // REFRESH ADDRESS LIST

        await fetchAddresses();
      } else {
        throw new Error(
          result?.message ||
            "Unable to update address."
        );
      }
    } catch (error) {
      console.error(
        "UPDATE ADDRESS ERROR:",
        error
      );

      setAddressError(
        error.message ||
          "Something went wrong while updating address."
      );
    } finally {
      setUpdatingAddress(false);
    }
  };

  // =====================================================
  // DELETE ADDRESS
  // DELETE /api/v1/addresses/:uuid
  // =====================================================

  const handleDeleteAddress = async (
    uuid
  ) => {
    const confirmDelete =
      window.confirm(
        "Are you sure you want to delete this address?"
      );

    if (!confirmDelete) {
      return;
    }

    try {
      setDeletingAddressId(uuid);
      setAddressError("");

      const token = getToken();

      if (!token) {
        setAddressError(
          "Login session nahi mili. Please login again."
        );
        return;
      }

      const response = await fetch(
        `${API}/addresses/${uuid}`,
        {
          method: "DELETE",

          headers: {
            Authorization:
              `Bearer ${token}`,

            "Content-Type":
              "application/json",
          },
        }
      );

      const result =
        await response.json();

      console.log(
        "DELETE ADDRESS RESPONSE:",
        result
      );

      if (!response.ok) {
        throw new Error(
          result?.message ||
            `Delete address failed (${response.status})`
        );
      }

      if (result.success === true) {
        alert(
          "Address deleted successfully!"
        );

        await fetchAddresses();
      } else {
        throw new Error(
          result?.message ||
            "Unable to delete address."
        );
      }
    } catch (error) {
      console.error(
        "DELETE ADDRESS ERROR:",
        error
      );

      setAddressError(
        error.message ||
          "Something went wrong while deleting address."
      );
    } finally {
      setDeletingAddressId(null);
    }
  };

  // =====================================================
  // TAB CHANGE
  // =====================================================

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === "addresses") {
      setShowAddressForm(false);
      setAddressError("");
      setEditingAddressId(null);
    }
  };

  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = async () => {
    const confirmLogout =
      window.confirm(
        "Are you sure you want to logout?"
      );

    if (!confirmLogout) {
      return;
    }

    await logout();

    navigate("/");
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      <Header />

      <div className="account-page">

        {/* =================================================
            ACCOUNT HEADER
        ================================================= */}

        <section className="account-top-section">

          <div className="account-container">

            <div className="account-profile-circle">
              <FaUser />
            </div>

            <div className="account-intro">

              <span>MY ACCOUNT</span>

              <h1>My Account</h1>

              <p>
                Manage your profile, orders
                and delivery addresses
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            ACCOUNT NAVIGATION
        ================================================= */}

        <div className="account-navigation">

          <div className="account-container account-nav-inner">

            {/* PROFILE */}

            <button
              type="button"
              className={
                activeTab === "profile"
                  ? "account-tab active"
                  : "account-tab"
              }
              onClick={() =>
                handleTabChange("profile")
              }
            >
              <FaUser />
              <span>My Profile</span>
            </button>


            {/* ORDERS */}

            <button
              type="button"
              className={
                activeTab === "orders"
                  ? "account-tab active"
                  : "account-tab"
              }
              onClick={() =>
                handleTabChange("orders")
              }
            >
              <FaShoppingBag />
              <span>Orders</span>
            </button>


            {/* ADDRESSES */}

            <button
              type="button"
              className={
                activeTab === "addresses"
                  ? "account-tab active"
                  : "account-tab"
              }
              onClick={() =>
                handleTabChange("addresses")
              }
            >
              <FaMapMarkerAlt />
              <span>Addresses</span>
            </button>


            {/* ACCOUNT SECURITY */}

            <button
              type="button"
              className={
                activeTab === "security"
                  ? "account-tab active"
                  : "account-tab"
              }
              onClick={() =>
                handleTabChange("security")
              }
            >
              <FaLock />
              <span>Account Security</span>
            </button>


            {/* LOGOUT */}

            <button
              type="button"
              className="account-tab"
              onClick={handleLogout}
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>

          </div>

        </div>


        {/* =================================================
            PROFILE SECTION
        ================================================= */}

        {activeTab === "profile" && (

          <main className="account-content">

            <div className="account-container">

              <div className="profile-information">

                <div className="section-heading">

                  <h2>
                    Profile Information
                  </h2>

                </div>

                <div className="information-grid">

                  <div className="information-item">
                    <label>FULL NAME</label>
                    <strong>{user?.name || "Not Added"}</strong>
                  </div>

                  <div className="information-item">
                    <label>EMAIL</label>
                    <strong>{user?.email || "Not Added"}</strong>
                  </div>

                  <div className="information-item">
                    <label>PHONE</label>
                    <strong>{user?.phone || "Not Added"}</strong>
                  </div>

                </div>

              </div>

            </div>

          </main>

        )}


     

{activeTab === "orders" && (
  <main className="account-content">
    <div className="account-container">

      <div className="account-section-card">

        <div className="section-heading">
          <div>
            <h2>Order History</h2>
            <p>View and track all your orders.</p>
          </div>
        </div>

        {/* ERROR */}

        {orderError && (
          <div className="address-error">
            {orderError}
          </div>
        )}

        {/* LOADING */}

        {loadingOrders ? (
          <div className="empty-state">
            <Loader label="Loading your orders..." />
          </div>
        ) : orders.length === 0 ? (

          /* EMPTY ORDERS */

          <div className="empty-state">

            <FaShoppingBag />

            <h3>No Orders Yet</h3>

            <p>
              You haven't placed any orders yet.
            </p>

            <button
              type="button"
              className="start-shopping-btn"
              onClick={() => navigate("/")}
            >
              Start Shopping
            </button>

          </div>

        ) : (

          /* ORDERS */

          <div className="orders-list">

            {orders.map((order) => (

              <div
                className="order-card"
                key={order.id || order.uuid || order.orderId}
              >

                <div className="order-card-header">

                  <div>
                    <span>ORDER NUMBER</span>

                    <strong>
                      #{order.orderNumber || order.id}
                    </strong>
                  </div>

                  <span className="order-status">
                    {order.status || "PLACED"}
                  </span>

                </div>

                <div className="order-card-body">

                  <div>
                    <span>ORDER DATE</span>

                    <strong>
                      {order.createdAt
                        ? new Date(
                            order.createdAt
                          ).toLocaleDateString()
                        : "N/A"}
                    </strong>
                  </div>

                  <div>
                    <span>TOTAL</span>

                    <strong>
                      ₹
                      {order.total ||
                        order.totalAmount ||
                        order.amount ||
                        0}
                    </strong>
                  </div>

                </div>

                <OrderBreakdown order={order} />

                <OrderActions
                  order={order}
                  token={getToken()}
                  onUpdated={(updated) => {
                    setOrders((prev) =>
                      prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
                    );
                    // the timeline has a new entry now - reload it next time it's opened
                    setTrackingByOrder((prev) => {
                      const next = { ...prev };
                      delete next[updated.id];
                      return next;
                    });
                  }}
                />

                <div className="order-card-footer">

                  <button
                    type="button"
                    className="view-order-btn"
                    onClick={() => handleViewOrder(order.id)}
                  >
                    {expandedOrderId === order.id
                      ? "HIDE TRACKING"
                      : "TRACK ORDER"}
                  </button>

                </div>

                {expandedOrderId === order.id && (
                  <div className="order-tracking-timeline">
                    {loadingTracking && !trackingByOrder[order.id] ? (
                      <p>Loading tracking...</p>
                    ) : trackingByOrder[order.id]?.timeline?.length ? (
                      trackingByOrder[order.id].timeline.map((event) => (
                        <div className="tracking-event" key={event.id}>
                          <strong>{event.statusLabel}</strong>
                          <span>
                            {new Date(event.createdAt).toLocaleString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p>No tracking updates yet.</p>
                    )}
                  </div>
                )}

              </div>

            ))}

          </div>

        )}

      </div>

    </div>
  </main>
)}


        {/* =================================================
            ADDRESSES SECTION
        ================================================= */}

        {activeTab === "addresses" && (

          <main className="account-content">

            <div className="account-container">

              <div className="address-section">

                {/* ADDRESS HEADER */}

                <div className="section-heading">

                  <div>

                    <h2>
                      Saved Addresses
                    </h2>

                    <p>
                      Manage your saved
                      delivery addresses.
                    </p>

                  </div>


                  <button
                    type="button"
                    className="add-address-btn"
                    onClick={() => {

                      setShowAddressForm(
                        (prev) => !prev
                      );

                      setAddressError("");

                    }}
                  >

                    <FaPlus />

                    {
                      showAddressForm
                        ? "CLOSE"
                        : "ADD NEW ADDRESS"
                    }

                  </button>

                </div>


                {/* ERROR */}

                {addressError && (

                  <div className="address-error">
                    {addressError}
                  </div>

                )}


                {/* =================================================
                    ADD ADDRESS FORM
                ================================================= */}

                {showAddressForm && (

                  <form
                    className="address-form"
                    onSubmit={
                      handleAddAddress
                    }
                  >

                    <h3>
                      Add New Address
                    </h3>


                    <div className="address-form-grid">

                      <div className="address-field">

                        <label>
                          FULL NAME
                        </label>

                        <input
                          type="text"
                          name="fullName"
                          value={
                            addressForm.fullName
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Enter full name"
                          required
                        />

                      </div>


                      <div className="address-field">

                        <label>
                          PHONE
                        </label>

                        <input
                          type="tel"
                          name="phone"
                          value={
                            addressForm.phone
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Enter phone number"
                          maxLength="10"
                          required
                        />

                      </div>


                      <div className="address-field full-width">

                        <label>
                          HOUSE / FLAT NO.
                        </label>

                        <input
                          type="text"
                          name="line1"
                          value={
                            addressForm.line1
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="House / flat number, building name"
                          required
                        />

                      </div>

                      <div className="address-field full-width">

                        <label>
                          STREET / AREA
                        </label>

                        <input
                          type="text"
                          name="line2"
                          value={
                            addressForm.line2
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Street, locality, area"
                          required
                        />

                      </div>

                      <div className="address-field full-width">

                        <label>
                          LANDMARK (OPTIONAL)
                        </label>

                        <input
                          type="text"
                          name="landmark"
                          value={
                            addressForm.landmark
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Near a temple, school, shop ..."
                          maxLength="150"
                        />

                      </div>


                      <div className="address-field">

                        <label>
                          CITY
                        </label>

                        <input
                          type="text"
                          name="city"
                          value={
                            addressForm.city
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Enter city"
                          required
                        />

                      </div>


                      <div className="address-field">

                        <label>
                          STATE
                        </label>

                        <input
                          type="text"
                          name="state"
                          value={
                            addressForm.state
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Enter state"
                          required
                        />

                      </div>


                      <div className="address-field">

                        <label>
                          PINCODE
                        </label>

                        <input
                          type="text"
                          name="pincode"
                          value={
                            addressForm.pincode
                          }
                          onChange={
                            handleAddressChange
                          }
                          placeholder="Enter pincode"
                          maxLength="6"
                          required
                        />

                      </div>

                    </div>


                    <div className="default-address-check">

                      <input
                        type="checkbox"
                        id="isDefault"
                        name="isDefault"
                        checked={
                          addressForm.isDefault
                        }
                        onChange={
                          handleAddressChange
                        }
                      />

                      <label htmlFor="isDefault">
                        Make this my default address
                      </label>

                    </div>


                    <button
                      type="submit"
                      className="save-address-btn"
                      disabled={
                        savingAddress
                      }
                    >

                      {
                        savingAddress
                          ? "SAVING..."
                          : "SAVE ADDRESS"
                      }

                    </button>

                  </form>

                )}


                {/* =================================================
                    SAVED ADDRESSES
                ================================================= */}

                <div className="saved-addresses">

                  {loadingAddresses ? (

                    <div className="address-loading">
                      <Loader label="Loading addresses..." size={64} />
                    </div>

                  ) : addresses.length === 0 ? (

                    <div className="empty-address">

                      <FaMapMarkerAlt />

                      <h3>
                        No Address Found
                      </h3>

                      <p>
                        You haven't added
                        any delivery
                        address yet.
                      </p>

                      <button
                        type="button"
                        className="add-address-small"
                        onClick={() => {

                          setShowAddressForm(
                            true
                          );

                          setAddressError("");

                        }}
                      >

                        <FaPlus />

                        Add Address

                      </button>

                    </div>

                  ) : (

                    <div className="address-grid">

                      {addresses.map(
                        (address) => (

                          <div
                            className="saved-address-card"
                            key={address.uuid}
                          >

                            {/* =================================================
                                CARD HEADER
                            ================================================= */}

                            <div className="saved-address-header">

                              <div className="address-user">

                                <FaUser />

                                <strong>
                                  {
                                    address.fullName
                                  }
                                </strong>

                              </div>


                              {address.isDefault && (

                                <span className="default-badge">

                                  <FaCheckCircle />

                                  DEFAULT

                                </span>

                              )}

                            </div>


                            {/* =================================================
                                ADDRESS DETAILS
                            ================================================= */}

                            <div className="saved-address-body">

                              <p>

                                <FaMapMarkerAlt />

                                {
                                  addressLine(address)
                                }

                              </p>


                              <p>

                                {
                                  address.city
                                }
                                ,{" "}
                                {
                                  address.state
                                }{" "}
                                -{" "}
                                {
                                  address.pincode
                                }

                              </p>


                              <p className="address-phone">

                                Phone:{" "}
                                {
                                  address.phone
                                }

                              </p>

                            </div>


                            {/* =================================================
                                ⭐ EDIT FORM
                                YAHI LAGAYA HAI
                            ================================================= */}

                            {editingAddressId ===
                              address.uuid && (

                              <form
                                className="edit-address-form"
                                onSubmit={
                                  handleUpdateAddress
                                }
                              >

                                <h4>
                                  Edit Address
                                </h4>


                                <div className="edit-address-grid">

                                  {/* FULL NAME */}

                                  <div className="address-field">

                                    <label>
                                      FULL NAME
                                    </label>

                                    <input
                                      type="text"
                                      name="fullName"
                                      value={
                                        editAddressForm.fullName
                                      }
                                      onChange={
                                        handleEditAddressChange
                                      }
                                      placeholder="Enter full name"
                                      required
                                    />

                                  </div>


                                  {/* PHONE */}

                                  <div className="address-field">

                                    <label>
                                      PHONE
                                    </label>

                                    <input
                                      type="tel"
                                      name="phone"
                                      value={
                                        editAddressForm.phone
                                      }
                                      onChange={
                                        handleEditAddressChange
                                      }
                                      placeholder="Enter phone number"
                                      maxLength="10"
                                      required
                                    />

                                  </div>


                                  {/* ADDRESS */}

                                  <div className="address-field full-width">

                                    <label>
                                      HOUSE / FLAT NO.
                                    </label>

                                    <input
                                      type="text"
                                      name="line1"
                                      value={
                                        editAddressForm.line1
                                      }
                                      onChange={
                                        handleEditAddressChange
                                      }
                                      placeholder="House / flat number, building name"
                                      required
                                    />

                                  </div>

                      <div className="address-field full-width">

                        <label>
                          STREET / AREA
                        </label>

                        <input
                          type="text"
                          name="line2"
                          value={
                            editAddressForm.line2
                          }
                          onChange={
                            handleEditAddressChange
                          }
                          placeholder="Street, locality, area"
                        />

                      </div>

                      <div className="address-field full-width">

                        <label>
                          LANDMARK (OPTIONAL)
                        </label>

                        <input
                          type="text"
                          name="landmark"
                          value={
                            editAddressForm.landmark
                          }
                          onChange={
                            handleEditAddressChange
                          }
                          placeholder="Near a temple, school, shop ..."
                          maxLength="150"
                        />

                      </div>

                                </div>


                                <div className="edit-default-address">

                                  <input
                                    type="checkbox"
                                    id={`edit-default-${address.uuid}`}
                                    name="isDefault"
                                    checked={
                                      editAddressForm.isDefault
                                    }
                                    onChange={
                                      handleEditAddressChange
                                    }
                                  />

                                  <label
                                    htmlFor={`edit-default-${address.uuid}`}
                                  >
                                    Make this my
                                    default address
                                  </label>

                                </div>

                                <div className="edit-address-buttons">

                                  <button
                                    type="button"
                                    className="cancel-edit-address"
                                    onClick={() => {

                                      setEditingAddressId(
                                        null
                                      );

                                      setEditAddressForm({
                                        fullName: "",
                                        phone: "",
                                        line1: "",
                                        line2: "",
                                        landmark: "",
                                        isDefault: false,
                                      });

                                      setAddressError("");

                                    }}
                                  >
                                    CANCEL
                                  </button>


                                  <button
                                    type="submit"
                                    className="save-edit-address"
                                    disabled={
                                      updatingAddress
                                    }
                                  >

                                    {
                                      updatingAddress
                                        ? "UPDATING..."
                                        : "UPDATE ADDRESS"
                                    }

                                  </button>

                                </div>

                              </form>

                            )}


                            <div className="saved-address-actions">

                              <button
                                type="button"
                                className="address-edit-btn"
                                onClick={() =>
                                  handleEditAddress(
                                    address
                                  )
                                }
                              >

                                <FaEdit />

                                EDIT

                              </button>


                              <button
                                type="button"
                                className="address-delete-btn"
                                onClick={() =>
                                  handleDeleteAddress(
                                    address.uuid
                                  )
                                }
                                disabled={
                                  deletingAddressId ===
                                  address.uuid
                                }
                              >

                                <FaTrash />

                                {
                                  deletingAddressId ===
                                  address.uuid
                                    ? "DELETING..."
                                    : "DELETE"
                                }

                              </button>

                            </div>

                          </div>

                        )
                      )}

                    </div>

                  )}

                </div>

              </div>

            </div>

          </main>

        )}

        {/* =================================================
            ACCOUNT SECURITY SECTION
        ================================================= */}

        {activeTab === "security" && (

          <main className="account-content">
            <div className="account-container">

              <div className="account-section-card">

                <div className="section-heading">
                  <div>
                    <h2>Account Security</h2>
                    <p>Change your password.</p>
                  </div>
                </div>

                {passwordError && (
                  <div className="address-error">{passwordError}</div>
                )}

                {passwordSuccess && (
                  <div className="address-success">{passwordSuccess}</div>
                )}

                <form
                  className="address-form"
                  onSubmit={handleChangePassword}
                >
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordFieldChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordFieldChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordFieldChange}
                    />
                  </div>

                  <button
                    type="submit"
                    className="save-profile-btn"
                    disabled={changingPassword}
                  >
                    {changingPassword ? "Saving..." : "Change Password"}
                  </button>
                </form>

              </div>

            </div>
          </main>

        )}

      </div>

      <Footer />

    </>
  );
};

export default Youraccount;